import React from 'react';
import { useLocation } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

import { useAuthenticationState } from '../../../store/useAuthenticationState';
import { Login } from './Login';
import { AutoLogout } from './AutoLogout';
import { useUserPermissions } from '../../../store/useUserPermissions';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { policyMatches } from '@/lib/policies';
import { ComplianceStatus } from './ComplianceStatus';
import { ComplianceItem } from '@/components/ui/TrainingListControl';
import { getBlockingComplianceItems, isAllowedComplianceRoute } from './complianceGate';

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      id
      name
      item
      type
      description
      pdf
      frequencyDays
      lastCompletionDate
      nextDueDate
      dueInDays
      status
      required
      source
      actionPath
      actionLabel
    }
  }
`;


export const ProtectedRoute = (params:any ) => {
    const { authenticationState } = useAuthenticationState();
    const { element, settings, children, permissions } = params;
    const location = useLocation();

    const { policies, isSecurityLoading } = useUserPermissions();
    const {
      data: complianceData,
      loading: isComplianceLoading,
      refetch: refetchCompliance
    } = useQuery<{ getUserComplianceStatus: ComplianceItem[] }>(GET_USER_COMPLIANCE_STATUS, {
      skip: !authenticationState,
      fetchPolicy: 'cache-first',
    });
    const blockingItems = getBlockingComplianceItems(complianceData?.getUserComplianceStatus || []);

    // Check permissions after user data is loaded
    const checkPermissions = () => {
      // If no permissions specified, allow access
      if (!permissions) {
        return true;
      }

      // If anyAuthenticated is true, allow access to any authenticated user
      if (permissions.anyAuthenticated === true) {
        return true;
      }

      // Wildcard super-view: *:*~* grants everything
      if (policies.includes('*:*~*')) {
        return true;
      }

      // Check required policies
      if (permissions.policies && permissions.policies.length > 0) {
        if (policies.some((userP: string) =>
              permissions.policies.some((reqP: string) => policyMatches(userP, reqP))
            )) {
          return true;
        }
      }

      // If we have permissions but user doesn't match any, deny access
      return false;
    };
  
    if (!authenticationState)      
      return <Login/>

    if (isSecurityLoading)
      return <OdrLoadingSpinner />;

    if (isComplianceLoading)
      return <OdrLoadingSpinner />;

    if (blockingItems.length > 0 && !isAllowedComplianceRoute(location.pathname, blockingItems)) {
      return (
        <ComplianceStatus
          items={blockingItems}
          onCleared={() => { void refetchCompliance(); }}
        />
      );
    }
    
    // Check permissions before rendering
    if (!checkPermissions()) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      );
    }
    
    return (
      <React.Fragment>
        <AutoLogout/>        
        {children}        
        
      </React.Fragment>
    )     
  }
 
